import { useContext, useState, useEffect } from "react";
import { AuthContext } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Users,
  ShieldCheck,
  User,
  Trash2,
  Crown
} from "lucide-react";
import { toast } from "sonner";

const TeamPage = () => {
  const { user, api } = useContext(AuthContext);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await api.get("/team");
      setTeam(response.data);
    } catch (error) {
      console.error("Error fetching team:", error);
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/team/${userId}/role`, { role: newRole });
      setTeam(team.map(member => 
        member.user_id === userId ? { ...member, role: newRole } : member
      ));
      toast.success("Role updated successfully");
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await api.delete(`/team/${userToDelete.user_id}`);
      setTeam(team.filter(member => member.user_id !== userToDelete.user_id));
      toast.success("Team member removed");
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error(error.response?.data?.detail || "Failed to remove team member");
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const getRoleBadge = (role) => {
    if (role === "admin") {
      return (
        <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
          <Crown className="w-3 h-3 mr-1" />
          Admin
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-slate-600">
        <User className="w-3 h-3 mr-1" />
        Team Member
      </Badge>
    );
  };

  const isAdmin = user?.role === "admin";

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="team-title">
            Team Management
          </h1>
          <p className="text-slate-500 font-body mt-1">
            Manage your team members and their roles
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="stat-total-members">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-body text-slate-500 uppercase tracking-wider">Total Members</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-2">{team.length}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="stat-admins">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-body text-slate-500 uppercase tracking-wider">Admins</p>
                  <p className="text-3xl font-heading font-bold text-amber-600 mt-2">
                    {team.filter(m => m.role === "admin").length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-sm flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="stat-team-members">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-body text-slate-500 uppercase tracking-wider">Team Members</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-2">
                    {team.filter(m => m.role === "team_member").length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                  <User className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Table */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="team-table-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
            <CardTitle className="text-lg font-heading font-semibold text-slate-900">Team Members</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : team.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Member</TableHead>
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Email</TableHead>
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Role</TableHead>
                    {isAdmin && (
                      <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs w-12"></TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team.map((member) => (
                    <TableRow 
                      key={member.user_id}
                      className="hover:bg-slate-50/50 transition-colors duration-200"
                      data-testid={`team-member-${member.user_id}`}
                    >
                      <TableCell className="font-body">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={member.picture} alt={member.name} />
                            <AvatarFallback className="bg-slate-100 text-slate-600 font-heading font-semibold">
                              {member.name?.charAt(0)?.toUpperCase() || "U"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{member.name}</p>
                            {member.user_id === user?.user_id && (
                              <span className="text-xs text-slate-500">(You)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-body text-sm text-slate-600">
                        {member.email}
                      </TableCell>
                      <TableCell>
                        {isAdmin && member.user_id !== user?.user_id ? (
                          <Select 
                            value={member.role} 
                            onValueChange={(value) => handleRoleChange(member.user_id, value)}
                          >
                            <SelectTrigger className="w-[150px] font-body text-sm" data-testid={`role-select-${member.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="team_member">Team Member</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          getRoleBadge(member.role)
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          {member.user_id !== user?.user_id && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setUserToDelete(member);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              data-testid={`delete-member-${member.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 p-8">
                <Users className="w-16 h-16 text-slate-200 mb-4" />
                <p className="text-slate-500 font-body">No team members found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        {!isAdmin && (
          <Card className="bg-slate-50 border border-slate-200 rounded-sm" data-testid="admin-info-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-slate-200 rounded-sm flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-body font-medium text-slate-900">Admin Access Required</p>
                  <p className="text-sm text-slate-500 font-body mt-1">
                    Only administrators can change roles or remove team members. Contact an admin if you need these permissions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Remove Team Member</AlertDialogTitle>
              <AlertDialogDescription className="font-body">
                Are you sure you want to remove <strong>{userToDelete?.name}</strong> from the team? 
                They will lose access to all clients and documents.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteUser}
                className="bg-rose-600 hover:bg-rose-700 text-white font-body"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default TeamPage;
